import { Badge } from "@/components/Badge";
import { Pill, StatPill, LabelPill } from "@/components/StatPill";
import { BarChart } from "@/components/BarChart";
import { Clock, Shield, Bot, Sparkles, Mail } from "lucide-react";

export const SendingTimeHeroSection = () => {
  return (
    <section className="hero-card">
      <div className="relative z-10">
        {/* Badge */}
        <div className="animate-fade-up" style={{ animationDelay: "100ms" }}>
          <Badge>Email Deliverability Deep Dive</Badge>
        </div>
        
        {/* Headline */}
        <div className="mt-6 sm:mt-8 animate-fade-up" style={{ animationDelay: "200ms" }}>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium text-primary">Spam Filter Research</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-[1.1] text-foreground">
            Why Sending Time Patterns
            <br />
            <span className="text-primary">Trigger Spam Filters</span>
          </h1>
          <p className="font-display text-xl sm:text-2xl text-muted-foreground mt-3 font-medium">
            Bots send at perfect intervals, humans don't
          </p>
        </div>
        
        {/* Subtitle */}
        <p className="text-muted-foreground text-base sm:text-lg leading-relaxed max-w-[700px] mt-5 animate-fade-up" style={{ animationDelay: "300ms" }}>
          Modern spam filters use AI-driven behavioral analysis to distinguish automated spam from legitimate human communication.
          <span className="font-semibold text-foreground"> Predictable timing patterns are a major detection signal</span>.
        </p>

        {/* Quick Stats Row - Using real data */}
        <div className="flex flex-wrap gap-4 sm:gap-6 mt-8 animate-fade-up" style={{ animationDelay: "400ms" }}>
          <div className="flex items-center gap-2.5 text-sm sm:text-base text-muted-foreground bg-muted/50 rounded-full px-4 py-2">
            <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            <span><strong className="text-foreground">84%</strong> global inbox rate</span>
          </div>
          <div className="flex items-center gap-2.5 text-sm sm:text-base text-muted-foreground bg-muted/50 rounded-full px-4 py-2">
            <Bot className="w-4 h-4 sm:w-5 sm:h-5 text-destructive" />
            <span><strong className="text-foreground">30-40%</strong> opens are bots</span>
          </div>
          <div className="flex items-center gap-2.5 text-sm sm:text-base text-muted-foreground bg-muted/50 rounded-full px-4 py-2">
            <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-success" />
            <span><strong className="text-foreground">0.3%</strong> spam threshold</span>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-6 sm:gap-8 mt-10 animate-fade-up" style={{ animationDelay: "500ms" }}>
          {/* Left column */}
          <div className="space-y-5">
            <Pill accent="2024-2025 Data">
              Based on Validity & GetResponse research
            </Pill>
            
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
              Gmail and Microsoft now deploy AI-based filtering systems that evaluate emails 
              for unnatural tone, repeated patterns, and mass-generated templates. Security 
              systems automatically click every link within milliseconds to check for phishing.
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              <StatPill value="87.2%" label="Gmail inbox rate" />
              <StatPill value="75.6%" label="Microsoft inbox" />
              <StatPill value="2-3 sec" label="bot click speed" />
              <StatPill value="12 sec" label="avg read time" />
            </div>
          </div>

          {/* Right column - Chart card with real ISP data */}
          <div className="accent-card">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between text-sm text-muted-foreground mb-4 gap-1">
              <span className="font-semibold text-foreground">Inbox Placement by Provider</span>
              <span className="pill text-[11px] py-0.5">Validity 2025</span>
            </div>

            <div className="space-y-4">
              <div>
                <LabelPill label="Gmail" value="87.2%" />
                <BarChart percentage={87} className="mt-1.5" />
              </div>

              <div>
                <LabelPill label="Yahoo / AOL" value="86.0%" />
                <BarChart percentage={86} className="mt-1.5" delay={100} />
              </div>

              <div>
                <LabelPill label="Microsoft (Outlook)" value="75.6%" />
                <BarChart percentage={76} variant="red" className="mt-1.5" delay={200} />
              </div>

              <p className="text-[11px] sm:text-xs text-muted-foreground pt-3 border-t border-border">
                Source: Validity 2024/2025 Email Deliverability Benchmark Report
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
